import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LogOut, FileDown, IdCard, AlertCircle } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import DocumentUpload from "@/components/DocumentUpload";

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);

  const isEN = i18n.language === "en";
  const docs = [
    { key: "id_card_front_url", label: isEN ? "ID front" : "Documento (fronte)", desc: isEN ? "ID card or passport" : "Carta d'identità o passaporto" },
    { key: "id_card_back_url", label: isEN ? "ID back" : "Documento (retro)" },
    { key: "license_front_url", label: isEN ? "Driving licence (front)" : "Patente (fronte)" },
    { key: "license_back_url", label: isEN ? "Driving licence (back)" : "Patente (retro)" },
  ];
  const missingCount = docs.filter((d) => !user?.[d.key]).length;

  const updateMyDocument = async (key, url) => {
    try {
      await api.patch("/auth/me", { [key]: url });
      await refresh();
      toast.success(url ? (isEN ? "Document uploaded" : "Documento caricato") : (isEN ? "Document removed" : "Documento rimosso"));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/contract/pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rent-lupi-contratto-${(user?.id || "doc").slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(i18n.language === "en" ? "Contract downloaded" : "Contratto scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setDownloading(false); }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <h1 className="font-display text-4xl font-bold tracking-tight">{t("profile.title")}</h1>

      <div className="border border-zinc-800 rounded-xl p-6 bg-zinc-900/40 space-y-3">
        <Field label={t("profile.name")} value={user?.name} testId="profile-name" />
        <Field label={t("profile.email")} value={user?.email} testId="profile-email" />
        <Field label={t("profile.phone")} value={user?.phone || "—"} />
        <Field label={t("profile.license")} value={user?.license_number || "—"} upper />
      </div>

      {user?.contract_accepted && (
        <div className="border border-[#FFD700]/30 bg-[#FFD700]/5 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FFD700]">
              {i18n.language === "en" ? "Signed contract" : "Contratto firmato"}
            </div>
            <div className="text-sm mt-1 text-zinc-300">
              {i18n.language === "en" ? "Accepted on" : "Accettato il"}{" "}
              <span className="font-medium">
                {new Date(user.contract_accepted_at).toLocaleString()}
              </span>
            </div>
          </div>
          <Button onClick={downloadPdf} disabled={downloading}
                  className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                  data-testid="download-contract-pdf">
            <FileDown className="w-4 h-4 mr-2" />
            {downloading
              ? (i18n.language === "en" ? "Downloading…" : "Scarico…")
              : (i18n.language === "en" ? "Download PDF" : "Scarica PDF")}
          </Button>
        </div>
      )}

      <Button variant="outline"
              className="w-full bg-zinc-900 border-zinc-800 hover:bg-zinc-800 text-zinc-200"
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="profile-logout-button">
        <LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}
      </Button>

      {/* My documents */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="my-documents-section">
        <div className="flex items-center gap-2 mb-4">
          <IdCard className="w-4 h-4 text-[#FFD700]" />
          <h2 className="font-semibold text-zinc-100">{isEN ? "My documents" : "I miei documenti"}</h2>
          {missingCount > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-amber-400 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
              <AlertCircle className="w-3 h-3" /> {missingCount} {isEN ? "missing" : "mancanti"}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          {isEN
            ? "Upload your ID and driving licence here so we can speed up the pickup at our office."
            : "Carica i tuoi documenti ora per velocizzare il ritiro del veicolo in sede. Verranno conservati nel tuo fascicolo digitale."}
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {docs.map((d) => (
            <DocumentUpload
              key={d.key}
              value={user?.[d.key]}
              onChange={(url) => updateMyDocument(d.key, url)}
              label={d.label}
              description={d.desc}
              testIdBase={`mydoc-${d.key.replace(/_/g, "-")}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, testId, upper }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 uppercase tracking-widest">{label}</div>
      <div className={`font-medium text-lg ${upper ? "uppercase" : ""}`} data-testid={testId}>{value}</div>
    </div>
  );
}
